import {
  createColumnHelper,
  PaginationState,
  RowSelectionState,
  SortingState,
} from "@tanstack/react-table";
import {
  Intent,
  KeywordSort,
  KeywordSortField,
  SearchType,
  SortOrder,
  Trend,
} from "../../../__generated__/gql/graphql";
import Table from "../../../ui/Table/Table";
import KeywordTableToolbar from "../KeywordTableToolbar/KeywordTableToolbar";
import styles from "./KeywordRecommendations.module.scss";
import KeywordCell from "../KeywordCell/KeywordCell";
import DifficultyCell from "../DifficultyCell/DifficultyCell";
import IntentCell from "../IntentCell/IntentCell";
import TrendCell from "../TrendCell/TrendCell";
import SearchTypeCell from "../SearchTypeCell/SearchTypeCell";
import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useSubscription } from "urql";
import { graphql } from "../../../__generated__/gql";
import { Hash, Refresh, Settings } from "../../../ui/Icon/Icon";
import Button from "../../../ui/Button/Button";
import { formatDistance } from "date-fns";
import urls from "../../../routes";
import { useNavigate } from "react-router-dom";
import Modal from "../../../ui/Modal/Modal";
import ConfirmationModal from "../../../ui/Modal/ConfirmationModal";
import { useDisclosure } from "../../../ui/util/useDisclosure";
import EmptyState from "../../../ui/EmptyState/EmptyState";
import Badge from "../../../ui/Badge/Badge";

function AddNewRecommendationsModal({
  isOpen,
  onClose,
  onSubmit,
  loading,
  newTopicsAndCompetitors,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  loading: boolean;
  newTopicsAndCompetitors: number;
}) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add new recommendations?"
      subtitle={`Macaw will add 25 new keyword ${
        newTopicsAndCompetitors === 1 ? "recommendation" : "recommendations"
      } from your topics and competitors sites`}
    >
      <ConfirmationModal
        loading={loading}
        confirmationText={`Continue (${newTopicsAndCompetitors} keyword ${
          newTopicsAndCompetitors === 1 ? "credit" : "credits"
        })`}
        onSubmit={onSubmit}
      />
    </Modal>
  );
}

const KeywordSettingsUpdatedSubscription = graphql(/* GraphQL */ `
  subscription keywordSettingsUpdated {
    keywordSettingsUpdated {
      initialKeywordRecommendationsLoading
      initialKeywordRecommendationsGenerated
    }
  }
`);

const KeywordRecommendationsQuery = graphql(/* GraphQL */ `
  query keywordRecommendations(
    $sort: KeywordSort
    $paginationOptions: PagePaginationOptions
  ) {
    newTopicsAndCompetitors
    areMoreKeywordRecommendationsAvailable
    keywordSettings {
      initialKeywordRecommendationsLoading
      initialKeywordRecommendationsGenerated
    }
    keywordRecommendations(sort: $sort, paginationOptions: $paginationOptions) {
      totalCount
      lastUpdatedAt
      nodes {
        keyword
        searchType
        searchValue
        difficulty
        searchVolume
        intent
        trend {
          date
          value
        }
      }
    }
  }
`);

const GetMoreKeywordRecommendationsMutation = graphql(/* GraphQL */ `
  mutation getMoreKeywordRecommendations {
    getMoreKeywordRecommendations {
      success
    }
  }
`);

type Result = {
  id: string;
  keyword: string;
  searchType?: SearchType | null;
  searchValue?: string | null;
  difficulty?: number | null;
  searchVolume?: number | null;
  intent?: Intent | null;
  trend?: Trend[] | null;
};

const columnHelper = createColumnHelper<Result>();

const columns = [
  columnHelper.accessor("keyword", {
    header: "Keyword",
    cell: KeywordCell,
    enableSorting: false,
    minSize: 200,
    meta: {
      tooltip: "The search term used on Google",
      sticky: true,
    },
  }),
  columnHelper.display({
    id: "searchType",
    header: "Topic",
    size: 190,
    enableSorting: false,
    cell: SearchTypeCell,
    meta: {
      tooltip: "The topic used to find this keyword",
    },
  }),
  columnHelper.accessor("difficulty", {
    header: "Difficulty",
    size: 130,
    cell: DifficultyCell,
    meta: {
      tooltip:
        "How difficult it is to rank for this keyword, from easy to hard. Anything below a 30 will be relatively easy, anything above a 70 will be very difficult",
    },
  }),
  columnHelper.accessor("searchVolume", {
    header: "Volume",
    size: 120,
    cell: ({ cell }) => {
      const value = cell.getValue();

      return value ? Intl.NumberFormat().format(value) : "-";
    },
    meta: {
      tooltip: "The estimated number of searches for this keyword each month",
    },
  }),
  columnHelper.accessor("intent", {
    header: "Intent",
    enableSorting: false,
    size: 168,
    cell: IntentCell,
    meta: {
      tooltip: "The search intent for this keyword",
    },
  }),
  columnHelper.accessor("trend", {
    header: "Trend",
    enableSorting: false,
    size: 120,
    cell: TrendCell,
    meta: {
      tooltip: "The history of the search volume over the past 12 months",
    },
  }),
];

const DEFAULT_PAGINATION_STATE: PaginationState = {
  pageIndex: 0,
  pageSize: 25,
};

function KeywordRecommendations() {
  const [keywordSettingsSubscription] = useSubscription({
    query: KeywordSettingsUpdatedSubscription,
  });

  const [addNewRecommendationsModalOpened, addNewRecommendationsModal] =
    useDisclosure();
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [pagination, setPagination] = useState<PaginationState>(
    DEFAULT_PAGINATION_STATE,
  );
  const [sort, setSort] = useState<KeywordSort>();
  const [debouncedSort, setDebouncedSort] = useState<KeywordSort>();
  const navigate = useNavigate();

  const [getMoreKeywordRecommendationsResponse, getMoreKeywordRecommendations] =
    useMutation(GetMoreKeywordRecommendationsMutation);

  const onSortingChange = useCallback((sortState: SortingState) => {
    const sort = sortState[0];

    if (!sort) {
      setSort(undefined);
      return;
    }

    const field = sort.id;
    const direction = sort.desc ? SortOrder.Desc : SortOrder.Asc;

    if (Object.values(KeywordSortField).includes(field as KeywordSortField)) {
      setSort({
        field: field as KeywordSortField,
        direction,
      });
    } else {
      setSort(undefined);
    }
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSort(sort);
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [sort]);

  const [searchByKeywordResponse, refetch] = useQuery({
    query: KeywordRecommendationsQuery,
    requestPolicy: "network-only",
    variables: {
      sort: debouncedSort,
      paginationOptions: {
        page: pagination.pageIndex,
      },
    },
  });

  const newTopicsAndCompetitors =
    searchByKeywordResponse.data?.newTopicsAndCompetitors ?? 0;
  const areMoreKeywordRecommendationsAvailable =
    searchByKeywordResponse.data?.areMoreKeywordRecommendationsAvailable ??
    false;

  const data =
    searchByKeywordResponse.data?.keywordRecommendations.nodes.map((node) => ({
      ...node,
      id: node.keyword,
    })) ?? [];

  const isSearching =
    !searchByKeywordResponse.data?.keywordSettings
      .initialKeywordRecommendationsGenerated &&
    searchByKeywordResponse.data?.keywordSettings
      .initialKeywordRecommendationsLoading;

  useEffect(() => {
    if (!isSearching && keywordSettingsSubscription.data) {
      refetch();
    }
  }, [isSearching, keywordSettingsSubscription.data, refetch]);

  return (
    <div className={styles.tableContainer}>
      <AddNewRecommendationsModal
        loading={getMoreKeywordRecommendationsResponse.fetching}
        isOpen={addNewRecommendationsModalOpened}
        onClose={addNewRecommendationsModal.close}
        newTopicsAndCompetitors={newTopicsAndCompetitors}
        onSubmit={async () => {
          await getMoreKeywordRecommendations({});

          addNewRecommendationsModal.close();
        }}
      />

      <Table
        disableSelectAll
        SelectedToolbar={KeywordTableToolbar}
        loading={searchByKeywordResponse.fetching}
        pagination={pagination}
        setPagination={(value) => {
          setPagination(value);
        }}
        totalCount={Math.min(
          searchByKeywordResponse.data?.keywordRecommendations.totalCount ?? 0,
        )}
        resourceName={{
          plural: "keyword recommendations",
          singular: "keyword recommendation",
        }}
        onSortingChange={(value) => {
          onSortingChange(value);
        }}
        enableSorting
        clearSelectionOnChange={false}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        data={data ?? []}
        isSelectable
        Header={
          <div className={styles.header}>
            <div className={styles.headerText}>
              <div className={styles.titleRow}>
                <h2 className={styles.title}>Keyword recommendations</h2>
              </div>
              <p className={styles.description}>
                Find keywords based on Macaw recommendations
              </p>
            </div>
            {!isSearching && (
              <div className={styles.headerRight}>
                {searchByKeywordResponse.data?.keywordRecommendations
                  .lastUpdatedAt && (
                  <span>
                    {`Updated ${formatDistance(
                      new Date(
                        searchByKeywordResponse.data?.keywordRecommendations.lastUpdatedAt,
                      ),
                      new Date(),
                      {
                        addSuffix: false,
                      },
                    )} ago`}
                  </span>
                )}
                <Button
                  variant="outline"
                  className={styles.refreshButton}
                  disabled={!areMoreKeywordRecommendationsAvailable}
                  loading={getMoreKeywordRecommendationsResponse.fetching}
                  onClick={async () => {
                    if (newTopicsAndCompetitors > 0) {
                      addNewRecommendationsModal.open();
                    } else {
                      await getMoreKeywordRecommendations({});
                    }
                  }}
                >
                  <Refresh size="medium" />
                </Button>
                <Button
                  variant="outline"
                  leftIcon={<Settings size="medium" />}
                  onClick={() =>
                    navigate(urls.settings.keywords.recommendations)
                  }
                >
                  Customize
                </Button>
              </div>
            )}
          </div>
        }
        emptyState={
          isSearching ? (
            <EmptyState
              icon={<Hash size="large" />}
              className={styles.emptyState}
              title="Keyword recommendations"
            >
              <Badge
                variant="pill"
                color="purple"
                size="small"
                showDot={false}
                loading
              >
                Searching
              </Badge>
            </EmptyState>
          ) : undefined
        }
      />
    </div>
  );
}

export default KeywordRecommendations;
